import React, { useState, useEffect, lazy, Suspense } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import HeroVideo from "./components/HeroVideo";
import VenueHighlightsBar from "./components/VenueHighlightsBar";
import BottomBar from "./components/BottomBar";
import StickyCTA from "./components/StickyCTA";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import "./App.css";

const Planning = lazy(() => import("./pages/Planning"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const CancellationPolicy = lazy(() => import("./components/CancellationPolicy"));
const About = lazy(() => import("./components/About"));
const Services = lazy(() => import("./components/Services"));
const GalleryCTA = lazy(() => import("./components/GalleryCTA"));
const Testimonials = lazy(() => import("./components/Testimonials"));
const Pricing = lazy(() => import("./components/Pricing"));
const FAQ = lazy(() => import("./components/FAQ"));
const ContactForm = lazy(() => import("./components/ContactForm"));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{background: 'red', color: 'white', padding: '20px', fontWeight: 'bold'}}>
          <h2>Something went wrong.</h2>
          <pre>{this.state.error && this.state.error.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showLoading, setShowLoading] = useState(true);

  // Scroll Progress Bar
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Loading Screen
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(false);
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
      <div className="App">
        {/* Loading Screen */}
        {showLoading && (
          <div className="loading-screen">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", color: "#8B0000" }}>
                SV Banquet Halls
              </h2>
              <p style={{ fontFamily: "'Noto Sans Telugu', sans-serif", marginTop: "10px", color: "#DAA520", fontSize: "1rem" }}>
                మీ కుటుంబం యొక్క శుభ క్షణాలకు స్వాగతం
              </p>
            </div>
          </div>
        )}

        {/* Scroll Progress Bar */}
        <div 
          className="scroll-progress" 
          style={{ transform: `scaleX(${scrollProgress / 100})` }}
        ></div>

        {/* Header component */}
        <Header />

        <Suspense fallback={null}>
          <Routes>
            <Route path="/planning" element={<Planning />} />
            <Route path="/planning/:slug" element={<Planning />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/" element={(
              <>
              <SEO
                title="SV Banquet Halls | Vendor-Freedom Banquet Hall in Mansoorabad, Hyderabad"
                description="Banquet hall in Mansoorabad where you choose your caterer, decorator & photographer. Zero mandatory packages. Families save ₹30K–₹70K with vendor freedom. 3 halls, 660 seated capacity. Telugu ceremonies specialist: బారసాల, లంగా ఓణీ వేడుక. Call +91 6305 333 751."
                canonical="https://www.svbanquethalls.com/"
              />
              <main id="main-content">
                <section id="hero-video">
                  <HeroVideo />
                </section>
                <div id="venue-highlights">
                  <VenueHighlightsBar />
                </div>
                <Suspense fallback={null}>
                  <section id="gallery">
                    <GalleryCTA />
                  </section>
                  <section id="about">
                    <About id="about" />
                  </section>
                  <section id="services">
                    <Services />
                  </section>
                  <section id="testimonials">
                    <Testimonials />
                  </section>
                  <section id="pricing">
                    <Pricing />
                  </section>
                  <section id="faq">
                    <FAQ />
                  </section>
                  <section id="contact">
                    <ContactForm />
                  </section>
                </Suspense>
              </main>
              </>)} />
          </Routes>
        </Suspense>

        {/* Footer, BottomBar, and StickyCTA */}
        <Footer />
        <BottomBar />
        <StickyCTA />
      </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
